import CorpConfig from './config';

/**
 * 接口混合加密客户端（主线程与 SharedWorker 均可用 Web Crypto）。
 * 每次请求：生成 AES-256-GCM 会话密钥 K，用后端 RSA 公钥(OAEP-SHA256)加密 K 放 X-Corp-Key 头；
 * 请求/响应 body 用 K 做 AES-GCM（密文尾部 16 字节为 tag，与后端对齐）。
 */

let pubKeyPromise: Promise<CryptoKey> | null = null;
const DEFAULT_FETCH_TIMEOUT_MS = 15000;

async function fetchAndReadWithTimeout<T>(
  url: string,
  init: RequestInit,
  readBody: (response: Response) => Promise<T>,
  timeoutMs = DEFAULT_FETCH_TIMEOUT_MS
) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {...init, signal: controller.signal});
    const body = await readBody(response);
    return {response, body};
  } finally {
    clearTimeout(timer);
  }
}

function ab2b64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for(let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function b642ab(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for(let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

function pemToDer(pem: string): ArrayBuffer {
  const body = pem
  .replace(/-----BEGIN [^-]+-----/, '')
  .replace(/-----END [^-]+-----/, '')
  .replace(/\s+/g, '');
  return b642ab(body);
}

function importPubKey(pem: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('spki', pemToDer(pem), {name: 'RSA-OAEP', hash: 'SHA-256'}, false, ['encrypt']);
}

function getServerPublicKey(): Promise<CryptoKey> {
  if(pubKeyPromise) return pubKeyPromise;
  pubKeyPromise = (async() => {
    const pinned = import.meta.env.VITE_CORP_PUBKEY;
    if(pinned && pinned.indexOf('BEGIN') !== -1) {
      return importPubKey(pinned);
    }
    const {body: data} = await fetchAndReadWithTimeout(
      CorpConfig.backendUrl + CorpConfig.endpoints.pubkey,
      {},
      (response) => response.json()
    );
    return importPubKey(data.publicKey);
  })().catch((err) => {
    pubKeyPromise = null; // 允许下次重试
    throw err;
  });
  return pubKeyPromise;
}

export interface SecureFetchOptions {
  method?: string;
  body?: any;
  headers?: Record<string, string>;
  timeoutMs?: number;
}

export interface SecureFetchResult {
  ok: boolean;
  status: number;
  data: any;
}

export async function secureFetch(path: string, opts: SecureFetchOptions = {}): Promise<SecureFetchResult> {
  const pubKey = await getServerPublicKey();

  const aesKey = await crypto.subtle.generateKey({name: 'AES-GCM', length: 256}, true, ['encrypt', 'decrypt']);
  const rawKey = await crypto.subtle.exportKey('raw', aesKey);
  const ek = ab2b64(await crypto.subtle.encrypt({name: 'RSA-OAEP'}, pubKey, rawKey));

  const headers: Record<string, string> = {'X-Corp-Key': ek, ...(opts.headers || {})};
  const method = opts.method || (opts.body !== undefined ? 'POST' : 'GET');
  let fetchBody: string | undefined;

  if(opts.body !== undefined) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plain = JSON.stringify({...opts.body, _ts: Date.now()});
    const ct = await crypto.subtle.encrypt({name: 'AES-GCM', iv}, aesKey, new TextEncoder().encode(plain));
    headers['Content-Type'] = 'application/json';
    fetchBody = JSON.stringify({v: 1, iv: ab2b64(iv.buffer), ct: ab2b64(ct)});
  }

  const {response: res, body: text} = await fetchAndReadWithTimeout(
    CorpConfig.backendUrl + path,
    {method, headers, body: fetchBody, keepalive: true},
    (response) => response.text(),
    opts.timeoutMs
  );

  let data: any;
  if(text) {
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch(err) {
      parsed = text;
    }
    if(parsed && typeof parsed === 'object' && typeof parsed.iv === 'string' && typeof parsed.ct === 'string') {
      const iv = new Uint8Array(b642ab(parsed.iv));
      const dec = await crypto.subtle.decrypt({name: 'AES-GCM', iv}, aesKey, b642ab(parsed.ct));
      const decStr = new TextDecoder().decode(dec);
      try {
        data = JSON.parse(decStr);
      } catch(err) {
        data = decStr;
      }
    } else {
      data = parsed; // 明文（如未加密的错误响应）
    }
  }

  return {ok: res.ok, status: res.status, data};
}
